export type VideoImportStatus =
	| 'pending'
	| 'downloading'
	| 'submitted'
	| 'processing'
	// Bunny: playable already, renditions still encoding — add-as-unit allowed.
	| 'transcoding'
	| 'ready'
	| 'failed'
	| 'unit_created';

export type VideoImportProvider = 'bunny' | 'vdocipher';
export type VideoImportSourceType = 'gdrive' | 'onedrive' | 'youtube' | 'direct';
export type VideoImportKind = 'video' | 'document';
/** Folder pastes only: which kinds to take (single files auto-detect). */
export type ImportInclude = 'both' | 'videos' | 'documents';

/** Statuses the background poll cares about — everything else is terminal. */
export const IN_FLIGHT_IMPORT_STATUSES: VideoImportStatus[] = [
	'pending',
	'downloading',
	'submitted',
	'processing',
	'transcoding',
];

/** A video_imports row as the admin backend serializes it (camelCase). */
export type VideoImport = {
	id: number;
	courseId: number;
	ownerType: 'admin' | 'instructor';
	ownerId: number;
	sourceType: VideoImportSourceType;
	sourceUrl: string;
	/** PDFs stream to the platform's storage — their provider is 'storage'. */
	provider: VideoImportProvider | 'storage';
	kind: VideoImportKind;
	/** Document rows only: S3 URL of the transferred file. */
	storageUri: string | null;
	providerVideoId: string | null;
	providerLibraryId: string | null;
	title: string;
	status: VideoImportStatus;
	errorMessage: string | null;
	/** Seconds; required before add-as-unit. */
	duration: number | null;
	thumbnailUrl: string | null;
	/** pg bigint — arrives as a string. */
	sizeBytes: string | number | null;
	/** Bunny only (0-100); null for VdoCipher. */
	encodeProgress: number | null;
	/** ffprobe-verified height of the downloaded source (YouTube worker only). */
	sourceHeight: number | null;
	/** Live worker stage ("Downloading — 420MB at 3.2MiB/s · ETA 01:23"). */
	stageDetail: string | null;
	unitId: number | null;
	retryCount: number;
	createdAt: string;
	updatedAt: string;
};

/** POST video-import/providers — enabled + configured booleans, no secrets. */
export type VideoImportCapabilities = {
	bunny: boolean;
	vdocipher: boolean;
	gdrive: boolean;
	/** Always true — OneDrive needs no credentials (anonymous shares API). */
	onedrive: boolean;
};

export type CreateVideoImportPayload = {
	course_id: number;
	source_url: string;
	provider: VideoImportProvider;
	/** Only meaningful when the URL turns out to be a OneDrive folder. */
	include?: ImportInclude;
	title?: string;
};

/** POST video-import/create-folder — one row per top-level video in the folder. */
export type CreateVideoImportFolderPayload = {
	course_id: number;
	folder_url: string;
	provider: VideoImportProvider;
	include?: ImportInclude;
};

export type CreateVideoImportFolderResult = {
	created: number;
	skipped: number;
	/** New files left in the folder beyond this take of 20 — paste again. */
	remaining: number;
	/** OneDrive "Add shortcut" children that can never import anonymously. */
	unsupported: number;
	imports: VideoImport[];
};

/** POST video-import/inspect-folder — row-free probe for the content chip. */
export type InspectVideoImportFolderPayload = {
	course_id: number;
	url: string;
};

export type InspectVideoImportFolderResult = {
	isFolder: boolean;
	sourceType: VideoImportSourceType;
	videos: number;
	documents: number;
	shortcuts: number;
	unsupportedShortcuts: number;
};

export type FetchVideoImportsPayload = {
	/** UI-only: background poll — no loading state, list updates only on change. */
	silent?: boolean;
	course_id: number;
	page?: number;
	per_page?: number;
};

export type GetVideoImportPayload = {
	id: number;
	course_id: number;
};

export type AddUnitVideoImportPayload = {
	id: number;
	course_id: number;
	name: string;
	order?: number;
	max_plays?: number;
};

export type AddUnitVideoImportResult = {
	unit: Record<string, any>;
	video: Record<string, any>;
	import: VideoImport;
};
